import React, { useState } from 'react';
import { 
  Play, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  RefreshCw,
  Server,
  Zap,
  Clock
} from 'lucide-react';
import { useQuery } from '@/apollo';
import { GET_SYSTEM_HEALTH, GET_SERVER_HEALTH_STATUS } from '../../graphql/system-health';
import { GET_SYSTEM_INFO } from '../../graphql/system-info';
import { Button } from '../ui/Button';
import { useNotification } from '../ui/Notification';

interface TestResult {
  name: string;
  status: 'pending' | 'running' | 'passed' | 'failed' | 'skipped';
  message?: string;
  duration?: number;
  timestamp?: Date;
}

interface TestSuite {
  name: string;
  description: string;
  tests: TestResult[];
  icon: React.ReactNode;
}

export const TestingDashboard: React.FC = () => {
  const { showNotification } = useNotification();
  const [testSuites, setTestSuites] = useState<TestSuite[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [runningTest, setRunningTest] = useState<string | null>(null);

  // System health query for health check tests
  const { refetch: refetchHealth } = useQuery(GET_SYSTEM_HEALTH, {
    errorPolicy: 'all',
    fetchPolicy: 'network-only'
  });

  const { data: serverHealthStatusData, refetch: refetchServerHealth } = useQuery(GET_SERVER_HEALTH_STATUS, {
    errorPolicy: 'all',
    fetchPolicy: 'network-only'
  });

  const { refetch: refetchSystemInfo } = useQuery(GET_SYSTEM_INFO, {
    errorPolicy: 'all',
    fetchPolicy: 'network-only'
  });

  // Initialize test suites
  React.useEffect(() => {
    setTestSuites([
      {
        name: 'System Health',
        description: 'Check server and database connectivity',
        icon: <Server className="w-5 h-5" />,
        tests: [
          { name: 'Server Health Check', status: 'pending' },
          { name: 'Database Connection', status: 'pending' },
          { name: 'GraphQL Endpoint', status: 'pending' },
          { name: 'API Response Time', status: 'pending' },
        ]
      },
      {
        name: 'Performance',
        description: 'Measure system performance metrics',
        icon: <Zap className="w-5 h-5" />,
        tests: [
          { name: 'Query Response Time', status: 'pending' },
          { name: 'Database Query Performance', status: 'pending' },
        ]
      },
    ]);
  }, []);

  const runTest = async (suiteIndex: number, testIndex: number): Promise<void> => {
    const suite = testSuites[suiteIndex];
    if (!suite) return;
    
    const test = suite.tests[testIndex];
    if (!test) return;
    
    setRunningTest(`${suite.name} - ${test.name}`);
    const startTime = Date.now();

    // Update test status to running
    setTestSuites(prev => {
      const updated = [...prev];
      if (updated[suiteIndex]?.tests[testIndex]) {
        updated[suiteIndex].tests[testIndex].status = 'running';
      }
      return updated;
    });

    try {
      let result: TestResult = { name: test.name, status: 'passed' };

      // Run specific test based on suite and test name
      switch (suite.name) {
        case 'System Health':
          switch (test.name) {
            case 'Server Health Check':
              await refetchServerHealth();
              if (serverHealthStatusData?.serverHealthStatus?.healthy) {
                result.message = 'Server is healthy';
              } else {
                throw new Error('Server health check failed');
              }
              break;
            
            case 'Database Connection':
              // Check database via GraphQL health query
              try {
                const graphqlUrl = '/graphql';
                const healthResponse = await fetch(graphqlUrl, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    query: `
                      query {
                        health {
                          status
                          checks {
                            database
                          }
                        }
                      }
                    `
                  })
                });
                
                if (healthResponse.ok) {
                  const healthResult = await healthResponse.json();
                  if (healthResult.data?.health?.checks?.database) {
                    result.message = 'Database connection successful';
                  } else {
                    throw new Error('Database connection failed');
                  }
                } else {
                  throw new Error(`GraphQL health query returned ${healthResponse.status}`);
                }
              } catch (error: any) {
                throw new Error(`Database connection test failed: ${error.message}`);
              }
              break;
            
            case 'GraphQL Endpoint':
              try {
                const response = await fetch('/graphql', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ query: '{ __typename }' })
                });
                if (response.ok) {
                  result.message = 'GraphQL endpoint is accessible';
                } else {
                  throw new Error(`GraphQL endpoint returned ${response.status}`);
                }
              } catch (error: any) {
                throw new Error(`GraphQL endpoint test failed: ${error.message}`);
              }
              break;
            
            case 'API Response Time':
              const apiStartTime = Date.now();
              await refetchHealth();
              const apiDuration = Date.now() - apiStartTime;
              result.message = `Response time: ${apiDuration}ms`;
              if (apiDuration > 5000) {
                throw new Error(`Response time too slow: ${apiDuration}ms`);
              }
              break;
          }
          break;


        case 'Performance':
          switch (test.name) {
            case 'Query Response Time':
              const queryStartTime = Date.now();
              await refetchSystemInfo();
              const queryDuration = Date.now() - queryStartTime;
              result.message = `Query time: ${queryDuration}ms`;
              if (queryDuration > 3000) {
                throw new Error(`Query too slow: ${queryDuration}ms`);
              }
              break;
            
            case 'Database Query Performance':
              const dbStartTime = Date.now();
              await refetchHealth();
              const dbDuration = Date.now() - dbStartTime;
              result.message = `Database query time: ${dbDuration}ms`;
              if (dbDuration > 2000) {
                throw new Error(`Database query too slow: ${dbDuration}ms`);
              }
              break;
            
          }
          break;
      }

      const duration = Date.now() - startTime;
      result.duration = duration;
      result.timestamp = new Date();

      // Update test result
      setTestSuites(prev => {
        const updated = [...prev];
        if (updated[suiteIndex]?.tests[testIndex]) {
          updated[suiteIndex].tests[testIndex] = result;
        }
        return updated;
      });

    } catch (error: any) {
      const duration = Date.now() - startTime;
      const result: TestResult = {
        name: test.name,
        status: 'failed',
        message: error.message || 'Test failed',
        duration,
        timestamp: new Date()
      };

      setTestSuites(prev => {
        const updated = [...prev];
        if (updated[suiteIndex]?.tests[testIndex]) {
          updated[suiteIndex].tests[testIndex] = result;
        }
        return updated;
      });
    } finally {
      setRunningTest(null);
    }
  };

  const runAllTests = async (): Promise<void> => {
    setIsRunning(true);
    setRunningTest('All Tests');

    try {
      for (let suiteIndex = 0; suiteIndex < testSuites.length; suiteIndex++) {
        const suite = testSuites[suiteIndex];
        if (!suite) continue;
        for (let testIndex = 0; testIndex < suite.tests.length; testIndex++) {
          await runTest(suiteIndex, testIndex);
          // Small delay between tests
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      // Calculate summary
      const allTests = testSuites.flatMap(suite => suite.tests);
      const passed = allTests.filter(t => t.status === 'passed').length;
      const failed = allTests.filter(t => t.status === 'failed').length;
      const skipped = allTests.filter(t => t.status === 'skipped').length;
      const total = allTests.length;

      showNotification(
        'success',
        'Tests Completed',
        `${passed}/${total} tests passed. ${failed} failed, ${skipped} skipped.`
      );
    } catch (error: any) {
      showNotification('error', 'Test Error', error.message || 'Failed to run tests');
    } finally {
      setIsRunning(false);
      setRunningTest(null);
    }
  };

  const runTestSuite = async (suiteIndex: number): Promise<void> => {
    setIsRunning(true);
    const suite = testSuites[suiteIndex];
    if (!suite) {
      setIsRunning(false);
      return;
    }
    setRunningTest(suite.name);

    try {
      for (let testIndex = 0; testIndex < suite.tests.length; testIndex++) {
        await runTest(suiteIndex, testIndex);
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      const suiteTests = suite.tests;
      const passed = suiteTests.filter(t => t.status === 'passed').length;
      const failed = suiteTests.filter(t => t.status === 'failed').length;

      showNotification(
        'success',
        `${suite.name} Completed`,
        `${passed}/${suiteTests.length} tests passed. ${failed} failed.`
      );
    } catch (error: any) {
      showNotification('error', 'Test Suite Error', error.message || 'Failed to run test suite');
    } finally {
      setIsRunning(false);
      setRunningTest(null);
    }
  };

  const resetTests = (): void => {
    setTestSuites(prev => prev.map(suite => ({
      ...suite,
      tests: suite.tests.map(test => ({
        name: test.name,
        status: 'pending' as const
      }))
    })));
  };

  const getStatusIcon = (status: TestResult['status']) => {
    switch (status) {
      case 'passed':
        return <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-success)' }} />;
      case 'failed':
        return <XCircle className="w-5 h-5" style={{ color: 'var(--color-error)' }} />;
      case 'running':
        return <RefreshCw className="w-5 h-5 animate-spin" style={{ color: 'var(--color-accent)' }} />;
      case 'skipped':
        return <AlertTriangle className="w-5 h-5" style={{ color: 'var(--color-warning)' }} />;
      default:
        return <Clock className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />;
    }
  };


  const allTests = testSuites.flatMap(suite => suite.tests);
  const passedCount = allTests.filter(t => t.status === 'passed').length;
  const failedCount = allTests.filter(t => t.status === 'failed').length;
  const skippedCount = allTests.filter(t => t.status === 'skipped').length;
  const runningCount = allTests.filter(t => t.status === 'running').length;
  const totalCount = allTests.length;

  return (
    <div className="space-y-6">
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
            System Testing
          </h2>
          <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
            Run diagnostic tests to verify system functionality and performance
          </p>
        </div>
        <div className="flex space-x-3">
          <Button
            variant="secondary"
            onClick={resetTests}
            disabled={isRunning}
            leftIcon={<RefreshCw className="w-4 h-4" />}
          >
            Reset
          </Button>
          <Button
            variant="primary"
            onClick={runAllTests}
            disabled={isRunning}
            isLoading={isRunning && runningTest === 'All Tests'}
            leftIcon={<Play className="w-4 h-4" />}
          >
            Run All Tests
          </Button>
        </div>
      </div>

      {/* Test Summary */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--color-bg-primary)', borderColor: 'var(--color-border)' }}>
          <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Total Tests</div>
          <div className="text-2xl font-bold mt-1" style={{ color: 'var(--color-text-primary)' }}>{totalCount}</div>
        </div>
        <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--color-bg-primary)', borderColor: 'var(--color-border)' }}>
          <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Passed</div>
          <div className="text-2xl font-bold mt-1" style={{ color: 'var(--color-success)' }}>{passedCount}</div>
        </div>
        <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--color-bg-primary)', borderColor: 'var(--color-border)' }}>
          <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Failed</div>
          <div className="text-2xl font-bold mt-1" style={{ color: 'var(--color-error)' }}>{failedCount}</div>
        </div>
        <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--color-bg-primary)', borderColor: 'var(--color-border)' }}>
          <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Skipped</div>
          <div className="text-2xl font-bold mt-1" style={{ color: 'var(--color-warning)' }}>{skippedCount}</div>
        </div>
        <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--color-bg-primary)', borderColor: 'var(--color-border)' }}>
          <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Running</div>
          <div className="text-2xl font-bold mt-1" style={{ color: 'var(--color-accent)' }}>{runningCount}</div>
        </div>
      </div>

      {/* Test Suites */}
      <div className="space-y-4">
        {testSuites.map((suite, suiteIndex) => (
          <div
            key={suiteIndex}
            className="rounded-xl border p-6"
            style={{ backgroundColor: 'var(--color-bg-primary)', borderColor: 'var(--color-border)' }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div style={{ color: 'var(--color-accent)' }}>{suite.icon}</div>
                <div>
                  <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                    {suite.name}
                  </h3>
                  <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                    {suite.description}
                  </p>
                </div>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => runTestSuite(suiteIndex)}
                disabled={isRunning}
                leftIcon={<Play className="w-4 h-4" />}
              >
                Run Suite
              </Button>
            </div>

            <div className="space-y-2">
              {suite.tests.map((test, testIndex) => (
                <div
                  key={testIndex}
                  className="flex items-center justify-between p-3 rounded-lg border"
                  style={{
                    backgroundColor: 'var(--color-bg-secondary)',
                    borderColor: 'var(--color-border)'
                  }}
                >
                  <div className="flex items-center space-x-3 flex-1">
                    {getStatusIcon(test.status)}
                    <div className="flex-1">
                      <div className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                        {test.name}
                      </div>
                      {test.message && (
                        <div className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                          {test.message}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    {test.duration !== undefined && (
                      <div className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                        {test.duration}ms
                      </div>
                    )}
                    {test.status === 'pending' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => runTest(suiteIndex, testIndex)}
                        disabled={isRunning}
                        leftIcon={<Play className="w-3 h-3" />}
                      >
                        Run
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Running Indicator */}
      {isRunning && runningTest && (
        <div className="fixed bottom-4 right-4 rounded-lg border p-4 shadow-lg" style={{ backgroundColor: 'var(--color-bg-primary)', borderColor: 'var(--color-border)', zIndex: 1000 }}>
          <div className="flex items-center space-x-3">
            <RefreshCw className="w-5 h-5 animate-spin" style={{ color: 'var(--color-accent)' }} />
            <div>
              <div className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                Running Tests
              </div>
              <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                {runningTest}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

